import React, { useEffect, useRef } from 'react';
import { Mail, Phone, MapPin, Linkedin, Github, Sparkles, Clock } from 'lucide-react';
import ContactForm from '../components/ContactForm';

const Contact = () => {
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.fade-in');
    elements.forEach((el) => observerRef.current.observe(el));

    return () => {
      if (observerRef.current) {
        elements.forEach((el) => observerRef.current.unobserve(el));
      }
    };
  }, []);

  const contactInfo = [
    {
      icon: <Mail size={24} />,
      title: 'Email',
      content: 'contact@bluepeak.com',
      link: 'mailto:contact@bluepeak.com',
      gradient: 'from-blue-500 to-cyan-500',
    },
    {
      icon: <Phone size={24} />,
      title: 'Phone',
      content: '+1 (555) 123-4567',
      link: 'tel:+15551234567',
      gradient: 'from-purple-500 to-pink-500',
    },
    {
      icon: <MapPin size={24} />,
      title: 'Location',
      content: '123 Digital Street\nSan Francisco, CA 94102\nUnited States',
      link: null,
      gradient: 'from-green-500 to-emerald-500',
    },
  ];

  return (
    <div className="pt-16 min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary-400 rounded-full mix-blend-multiply filter blur-3xl animate-float"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
        <div className="text-center mb-12 fade-in">
          <div className="inline-flex items-center gap-2 mb-4">
            <Sparkles className="text-primary-600 animate-sparkle" size={24} />
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900">
              Get In <span className="bg-gradient-to-r from-primary-600 to-purple-600 bg-clip-text text-transparent">Touch</span>
            </h1>
            <Sparkles className="text-primary-600 animate-sparkle" size={24} style={{ animationDelay: '1s' }} />
          </div>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Have a project in mind? We'd love to hear from you. Send us a message and we'll
            respond as soon as possible.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div className="fade-in bg-white rounded-xl shadow-xl p-8 border border-gray-100 hover:shadow-2xl transition-all duration-300">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <span className="w-1 h-8 bg-gradient-to-b from-primary-600 to-purple-600 rounded"></span>
                Contact Information
              </h2>
              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div
                    key={index}
                    className="group flex items-start gap-4 p-4 rounded-lg hover:bg-gray-50 transition-all duration-300"
                  >
                    <div className={`relative p-4 rounded-xl bg-gradient-to-br ${info.gradient} flex-shrink-0 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                      <div className="text-white relative z-10">{info.icon}</div>
                      <div className="absolute inset-0 bg-white/20 rounded-xl blur-lg group-hover:blur-xl transition-all"></div>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-primary-600 transition-colors">
                        {info.title}
                      </h3>
                      {info.link ? (
                        <a
                          href={info.link}
                          className="text-primary-600 hover:text-primary-700 transition-colors hover:underline"
                        >
                          {info.content}
                        </a>
                      ) : (
                        <p className="text-gray-600 whitespace-pre-line">{info.content}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <span>Follow Us</span>
                  <Sparkles size={16} className="text-primary-600 animate-sparkle" />
                </h3>
                <div className="flex gap-4">
                  {[
                    { icon: <Linkedin size={24} />, href: 'https://linkedin.com', label: 'LinkedIn' },
                    { icon: <Github size={24} />, href: 'https://github.com', label: 'GitHub' },
                  ].map((social, index) => (
                    <a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group relative p-4 bg-primary-100 rounded-lg text-primary-600 hover:bg-primary-200 transition-all duration-300 hover:scale-110 tech-glow"
                    >
                      {social.icon}
                      <span className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        {social.label}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="fade-in bg-gradient-to-br from-primary-50 to-purple-50 rounded-xl p-6 border-2 border-primary-200 hover:shadow-lg transition-all duration-300">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="text-primary-600" size={24} />
                <h3 className="font-semibold text-gray-900">Business Hours</h3>
              </div>
              <div className="text-gray-700 space-y-2">
                <p className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-primary-600 rounded-full"></span>
                  Monday - Friday: 9:00 AM - 6:00 PM PST
                </p>
                <p className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-primary-600 rounded-full"></span>
                  Saturday: 10:00 AM - 4:00 PM PST
                </p>
                <p className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                  Sunday: Closed
                </p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="fade-in" style={{ animationDelay: '0.2s' }}>
            <div className="bg-white rounded-xl shadow-xl p-8 border border-gray-100 hover:shadow-2xl transition-all duration-300">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <span className="w-1 h-8 bg-gradient-to-b from-primary-600 to-purple-600 rounded"></span>
                Send Us a Message
              </h2>
              <ContactForm />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
